'use client'

import React, { useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { AnimatedGroup } from '@/components/ui/animated-group'
import { HeroHeader } from '@/components/header'
import Footer from '@/components/footer'
import { 
  Mail, 
  Phone, 
  MapPin,
  MessageSquare,
  Send,
  Clock,
  Users,
  Building,
  ArrowRight,
  CheckCircle,
  Loader2
} from 'lucide-react'

// Using design.json color palette
const contactMethods = [
  {
    icon: Mail,
    title: "Email Support",
    description: "Get help from our support team",
    contact: "support@socialloop.com",
    availability: "24/7 response within 4 hours",
    bgColor: "bg-[#ffaf68]", // Orange from design.json
    rotateClass: "rotate-2"
  },
  {
    icon: Phone,
    title: "Phone Support",
    description: "Speak directly with our team",
    contact: "+1 (555) 123-4567",
    availability: "Mon-Fri, 9AM-6PM PST",
    bgColor: "bg-[#70b5ff]", // Blue from design.json
    rotateClass: "-rotate-1"
  },
  {
    icon: MessageSquare,
    title: "Live Chat",
    description: "Instant help when you need it",
    contact: "Available in-app",
    availability: "Mon-Fri, 9AM-6PM PST",
    bgColor: "bg-[#79d45e]", // Green from design.json
    rotateClass: "rotate-3"
  },
  {
    icon: MapPin,
    title: "Office Location",
    description: "Visit us in person",
    contact: "123 Innovation Drive, San Francisco, CA 94105",
    availability: "By appointment only",
    bgColor: "bg-[#f4889a]", // Pink from design.json
    rotateClass: "-rotate-2"
  }
]

const inquiryTypes = [
  { value: "general", label: "General Inquiry" },
  { value: "sales", label: "Sales & Pricing" },
  { value: "support", label: "Technical Support" },
  { value: "partnership", label: "Partnership Opportunities" },
  { value: "press", label: "Press & Media" },
  { value: "careers", label: "Careers" }
]

const transitionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    setIsSubmitting(false)
    setIsSubmitted(true)
  }

  return (
    <div className="w-full" style={{ backgroundColor: '#fdf1d3' }}> {/* Background from design.json */}
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Scattered decorative elements following design.json */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-[#4928fd] border-4 border-black transform rotate-45 shadow-xl"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-[#ba81ee] border-4 border-black rounded-full shadow-xl"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-12 bg-[#79d45e] border-4 border-black transform -rotate-12 shadow-xl"></div>
        <div className="absolute bottom-48 right-1/3 w-10 h-6 bg-[#70b5ff] border-4 border-black transform rotate-12 shadow-xl"></div>

        {/* Floating sticky notes */}
        <div className="absolute top-32 right-4 bg-[#f6e683] border-4 border-black p-3 transform rotate-12 shadow-xl max-w-32 hidden md:block">
          <p className="text-xs font-black text-black">"We're here to help!"</p>
        </div>

        {/* Hero Section */}
        <section className="relative px-6 py-16 md:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <Badge variant="outline" className="border-white text-white bg-transparent">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  GET IN TOUCH
                </Badge>
              </div>
            </AnimatedGroup>

            <div className="bg-[#f4889a] border-4 border-black p-8 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-black leading-tight">
                LET'S START A<br />CONVERSATION
              </h1>
            </div>

            <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-3xl">
              <p className="text-lg font-semibold text-black">
                Have questions about SocialLoop? Want to see a demo? Ready to transform your networking events? We're here to help you get started.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {contactMethods.map((method, index) => (
                <AnimatedGroup
                  key={method.title}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`h-full text-center ${method.bgColor} border-4 border-black transform hover:rotate-0 transition-all duration-200 shadow-xl p-6 ${method.rotateClass}`}>
                    <div className="mx-auto mb-4 w-12 h-12 bg-white border-4 border-black rounded-full flex items-center justify-center">
                      <method.icon className="h-6 w-6 text-black" />
                    </div>
                    <h3 className="text-lg font-black text-black mb-2">{method.title}</h3>
                    <p className="text-black font-semibold mb-4">{method.description}</p>
                    <p className="font-black text-black mb-2">{method.contact}</p>
                    <p className="text-sm font-semibold text-black/80">{method.availability}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -top-2 -right-2 w-5 h-5 bg-white border-2 border-black transform rotate-45"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-black rounded-full"></div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#d9f3fc' }}>
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-12 lg:grid-cols-2">
              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div>
                  <div className="bg-[#79d45e] border-4 border-black p-6 transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                    <h2 className="text-3xl md:text-4xl font-black text-black">Send us a Message</h2>
                  </div>
                  
                  <div className="bg-white border-4 border-black p-6 transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8">
                    <p className="text-lg font-semibold text-black">
                      Fill out the form and we'll get back to you within 24 hours. For urgent matters, please use our live chat or phone support.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 bg-[#ffaf68] border-4 border-black p-3 transform rotate-1">
                      <Clock className="h-5 w-5 text-black" />
                      <span className="text-sm font-black text-black">Response time: Within 24 hours</span>
                    </div>
                    <div className="flex items-center gap-3 bg-[#70b5ff] border-4 border-black p-3 transform -rotate-1">
                      <Users className="h-5 w-5 text-black" />
                      <span className="text-sm font-black text-black">Dedicated support team</span>
                    </div>
                    <div className="flex items-center gap-3 bg-[#ba81ee] border-4 border-black p-3 transform rotate-2">
                      <Building className="h-5 w-5 text-black" />
                      <span className="text-sm font-black text-black">Enterprise solutions available</span>
                    </div>
                  </div>
                </div>
              </AnimatedGroup>

              <AnimatedGroup variants={{ item: transitionVariants }}>
                <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200 shadow-xl">
                  <div className="p-6">
                    <div className="bg-[#4928fd] border-4 border-black p-4 mb-6">
                      <h3 className="text-xl font-black text-white">Contact Form</h3>
                      <p className="text-white font-semibold">
                        Tell us about your needs and we'll help you find the perfect solution.
                      </p>
                    </div>
                    
                    {isSubmitted ? (
                      <div className="text-center py-8">
                        <CheckCircle className="h-16 w-16 text-[#79d45e] mx-auto mb-4" />
                        <h3 className="text-xl font-black text-black mb-2">Message Sent!</h3>
                        <p className="font-semibold text-black mb-6">
                          Thank you for contacting us. We'll get back to you within 24 hours.
                        </p>
                        <div className="bg-[#79d45e] border-4 border-black transform rotate-1">
                          <Button 
                            onClick={() => setIsSubmitted(false)}
                            className="bg-[#79d45e] hover:bg-[#79d45e]/90 text-black border-0 font-black">
                            Send Another Message
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <Label htmlFor="name" className="font-black text-black">Full Name *</Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="Your full name"
                              className="border-4 border-black font-semibold"
                            />
                          </div>
                          <div>
                            <Label htmlFor="email" className="font-black text-black">Email Address *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              placeholder="your@email.com"
                              className="border-4 border-black font-semibold"
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="company" className="font-black text-black">Company</Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            placeholder="Your company name"
                            className="border-4 border-black font-semibold"
                          />
                        </div>

                        <div>
                          <Label htmlFor="inquiryType" className="font-black text-black">Inquiry Type *</Label>
                          <select
                            id="inquiryType"
                            name="inquiryType"
                            value={formData.inquiryType}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border-4 border-black bg-white font-semibold text-black">
                            <option value="">Select inquiry type</option>
                            {inquiryTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label htmlFor="message" className="font-black text-black">Message *</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            placeholder="Tell us about your needs, questions, or how we can help..."
                            rows={5}
                            className="border-4 border-black font-semibold"
                          />
                        </div>

                        <div className="bg-[#4928fd] border-4 border-black transform rotate-1 hover:rotate-0 transition-transform duration-200">
                          <Button 
                            type="submit" 
                            className="w-full bg-[#4928fd] hover:bg-[#4928fd]/90 text-white border-0 font-black py-3" 
                            disabled={isSubmitting}>
                            {isSubmitting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sending Message...
                              </>
                            ) : (
                              <>
                                <Send className="mr-2 h-4 w-4" />
                                Send Message
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </AnimatedGroup>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#ba81ee' }}>
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#ffaf68] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-black">
                  Ready to Get Started?
                </h2>
              </div>
              <div className="bg-white border-4 border-black p-4 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8">
                <p className="text-lg font-semibold text-black">
                  Don't wait - start creating meaningful connections at your next event today.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="bg-[#4928fd] border-4 border-black transform rotate-2 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-[#4928fd] hover:bg-[#4928fd]/90 text-white border-0 font-black" asChild>
                    <Link href="/signup">
                      Start Free Trial
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                    <Link href="/pricing">
                      View Pricing
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedGroup>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}